import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { PrismButton, PrismDialog } from '@prism-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
  faExclamationTriangle,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import ApplicationHeader from '../ApplicationHeader';
import SaveAndStepButton from '../SaveAndStepButton.jsx';
import SubmittedApp from '../SubmittedApp/index.jsx';
import ApplicationWindowContext from '../ApplicationContainer/ApplicationWindowContext';
import { formatDate } from '../../../util/dateUtils';
import {
  APPLICANT_TYPE_MENTEE,
  APPLICANT_TYPE_MENTOR,
  APPLICANT_TYPE_COMMITTEE,
} from '../constants';

const ReviewApplicationStep = ({
  application,
  jumpToStep,
  saveApplication,
  currentUser,
  refreshApplicationState,
  clearDraft,
}) => {
  var thisPageStep = 7;

  if (application?.applicant_type_id === 1) {
    thisPageStep = 6;
  }

  const [showPopup, setShowPopup] = useState(false);
  const [managerApprovalRequired, setManagerApprovalRequired] = useState(false);

  const { soft_deadline, hard_deadline } = useContext(ApplicationWindowContext);
  const closeDate = soft_deadline || hard_deadline;
  const closeDateFormatted = closeDate ? formatDate(closeDate) : null;

  useEffect(() => {
    const chapterId = application?.chapter_id;
    if (!chapterId) return;

    axios.get(`/api/manager_approval_type_config/${chapterId}`)
      .then((res) => {
        const config = res.data;
        if (!config) {
          // No config = default behavior (mentee required)
          setManagerApprovalRequired(application.applicant_type_id === APPLICANT_TYPE_MENTEE);
          return;
        }
        const typeId = application.applicant_type_id;
        if (typeId === APPLICANT_TYPE_MENTEE) setManagerApprovalRequired(config.mentee_required);
        else if (typeId === APPLICANT_TYPE_MENTOR) setManagerApprovalRequired(config.mentor_required);
        else if (typeId === APPLICANT_TYPE_COMMITTEE) setManagerApprovalRequired(config.tcm_required);
      })
      .catch(() => {
        // If fetch fails, default to showing for mentees
        setManagerApprovalRequired(application.applicant_type_id === APPLICANT_TYPE_MENTEE);
      });
  }, [application?.chapter_id, application?.applicant_type_id]);

  const onContinue = async () => {
    setShowPopup(true);
  };

  const onBack = async () => {
    jumpToStep(thisPageStep - 1);
  };

  const handleFormSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    await saveApplication({ ...application });
  };

  const handleApplicationSubmit = async (event) => {
    event.preventDefault();
    console.log('=== SUBMITTING APPLICATION ===');

    const submitted = {
      ...application,
      submitted_at: new Date(),
      status_id: 2, // submitted status
      last_step_index: null, // clear draft progress
      ApplicationStatus: {
        id: 2,
        status: 'Submitted',
      },
    };

    await saveApplication(submitted);

    // Dispatch custom event to notify header
    console.log('Dispatching applicationUpdated event');
    window.dispatchEvent(
      new CustomEvent('applicationUpdated', {
        detail: { message: 'Application submitted' },
      })
    );

    // Refresh state from server to prevent stale data
    await refreshApplicationState();
    if (clearDraft) clearDraft();
    jumpToStep(0);
  };

  const applicant_type = (application.ApplicantType?.applicant_type || '')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  return (
    <div>
      <PrismDialog
        open={showPopup}
        close-method="user"
        onPrismClose={() => setShowPopup(false)}
      >
        <span slot="header">
          <FontAwesomeIcon icon={faExclamationTriangle} /> Ready to submit?
        </span>
        <p>
            {closeDateFormatted
              ? <>You can modify your application until <strong>{closeDateFormatted}</strong>.{' '}</>
              : 'You can modify your application until the application window closes. '}
            If you have questions or would like someone to review your application,
            please reach out to the CLEAR committee in the{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://app.slack.com/client/T024VU91V/CGKTPBYKG"
            >
              #clear
            </a>{' '}
            slack channel.
          </p>
          <p style={{ marginTop: '8px', fontSize: '0.92em', display: managerApprovalRequired ? undefined : 'none' }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px' }} />
              Your manager{application.manager ? ` (${application.manager})` : ''} will
              be automatically notified to review and approve your participation.
          </p>
        <div slot="footer" style={{ display: 'flex', gap: '0.5rem' }}>
          <PrismButton
            tone="primary-brand"
            onPrismClick={handleApplicationSubmit}
          >
            Submit {applicant_type} Application
          </PrismButton>
          <PrismButton display="outline" onPrismClick={() => setShowPopup(false)}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      <ApplicationHeader
        heading="Review Your Submission"
        message="Please review your application. The submission button is located at the bottom of the page."
      />

      {managerApprovalRequired && (
        <div style={{
          backgroundColor: 'var(--color-surface-alt)',
          border: '1px solid var(--color-border)',
          borderRadius: '6px',
          padding: '12px 16px',
          marginTop: '1.5em',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          color: 'var(--color-text)',
        }}>
          <FontAwesomeIcon icon={faEnvelope} style={{ marginTop: '3px', color: 'var(--color-primary)' }} />
          <div>
            <strong>Manager Approval Required</strong>
            <p style={{ margin: '4px 0 0', fontSize: '0.92em' }}>
              When you submit this application, your manager
              {application.manager ? ` (${application.manager})` : ''} will be
              automatically notified via email to review and approve your
              participation in the CLEAR program.
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          border: '1px solid var(--color-border)',
          padding: '1em',
          marginTop: '3em',
          borderRadius: '5px',
        }}
      >
        <SubmittedApp
          params={{ application_id: application.id }}
          currentUser={currentUser}
          applicationStatus={application.ApplicationStatus}
          hideHeader
        />
      </div>

      <form onSubmit={handleFormSubmit}>
        <div>
          <div className="nav-btns">
            <SaveAndStepButton
              buttonText="Back"
              icon={faChevronLeft}
              iconPosition="left"
              onSave={handleFormSubmit}
              onSuccessFunction={onBack}
            />
            <SaveAndStepButton
              lastStep
              buttonText={`Submit ${applicant_type} Application`}
              icon={faChevronRight}
              iconPosition="right"
              onSave={handleFormSubmit}
              onSuccessFunction={onContinue}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ReviewApplicationStep;
